import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import InnerHTML from 'dangerously-set-html-content';

import Event from './Event.jsx';

import { eventPropType } from '../propTypes';
import isoToDate from '../../../utils/isoToDate.js';

const dateTimeFormat = 'yyyy-MM-dd HH:mm:ss';

function EventListing({
  activeCategories, adBasement = '', ads = [], currentDay, currentTimeOverride = '', currentTimezone, eventsByDay,
}) {
  const currentDateTime = currentTimeOverride
    ? DateTime.fromFormat(currentTimeOverride, dateTimeFormat, { zone: 'Australia/Melbourne' }).setZone(currentTimezone)
    : DateTime.local().setZone(currentTimezone);

  const formatDate = (date) => isoToDate(date, currentTimezone);

  const savedEvents = useSelector((state) => state.savedEvents);

  const groupByDays = ['saved', 'shared'].includes(currentDay);
  const eventsToDisplay = groupByDays ? eventsByDay : eventsByDay.filter((day) => day.date === currentDay);

  const getEventsGroupedByDays = () => {
    const params = Object.fromEntries(new URLSearchParams(window.location.search));

    let eventIds = [];

    if (currentDay === 'saved') {
      eventIds = savedEvents;
    }

    if (currentDay === 'shared') {
      ({ eventIds } = params);
    }

    if (eventIds === 0) return <p>No events found.</p>;

    const eventsFilteredByIds = eventsToDisplay.map((day) => ({
      ...day,
      events: day.events.filter(({ id }) => eventIds.includes(id)),
    }));

    const eventsFilteredByCategory = eventsFilteredByIds.map((day) => ({
      ...day,
      events: activeCategories.length > 0
        ? day.events.filter(({ eventCategories }) => eventCategories
          .some((cat) => activeCategories.includes(cat.toString())))
        : day.events,
    }));

    const filteredDays = eventsFilteredByCategory.filter((day) => day.events.length > 0);

    if (filteredDays.length === 0) return <p>No events found.</p>;

    return (
      eventsFilteredByCategory.filter((day) => day.events.length > 0).map((day) => {
        const dateSplit = formatDate(day.date).split(',');

        return (
          <div className="c-event-schedule__day-group" key={day.id}>
            <div className="c-event-schedule__day-group-header">
              <h3 className="c-event-schedule__day-name">{dateSplit[0]}</h3>
              <span className="c-event-schedule__day-date">{`${dateSplit[1]}, ${dateSplit[2]}`}</span>
            </div>

            {day.events.map((event) => (
              <Event
                currentDateTime={currentDateTime}
                currentTimezone={currentTimezone}
                key={`${event.id}-${event.startTime}`}
                {...event} // eslint-disable-line react/jsx-props-no-spreading
              />
            ))}
          </div>
        );
      })
    );
  };

  const getEventsNotGroupedByDays = () => {
    const filteredEvents = activeCategories.length > 0
      ? eventsToDisplay[0].events
        .filter(({ eventCategories }) => eventCategories.some((cat) => activeCategories.includes(cat.toString())))
      : eventsToDisplay[0].events;

    if (filteredEvents.length === 0) return <p>No events found.</p>;

    return (
      filteredEvents.map((event, i) => (
        <React.Fragment key={`${event.id}-${event.startTime}`}>
          <Event
            currentDateTime={currentDateTime}
            currentTimezone={currentTimezone}
            {...event} // eslint-disable-line react/jsx-props-no-spreading
          />

          {(i + 1) % 3 === 0 && ads[((i + 1) / 3) - 1] && (
          // eslint-disable-next-line react/no-danger
          <div className="c-event-schedule__ad">
            <InnerHTML html={ads[((i + 1) / 3) - 1]} allowRerender="True" />
          </div>
          )}

          {adBasement && (i + 1) === filteredEvents.length && (
            <div className="c-event-schedule__ad">
              <InnerHTML html={adBasement} allowRerender="True" />
            </div>
          )}
        </React.Fragment>
      ))
    );
  };

  return (
    <section className="o-section">
      <div className="o-container">
        <div className="o-col o-col--10-at-small o-col--offset-1-at-small o-col--6-at-large o-col--offset-6-at-large o-col--7-at-xlarge o-col--offset-5-at-xlarge">
          <div className="o-col__inner">
            {groupByDays ? getEventsGroupedByDays() : getEventsNotGroupedByDays()}
          </div>
        </div>
      </div>
    </section>
  );
}

EventListing.propTypes = {
  activeCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  adBasement: PropTypes.string,
  ads: PropTypes.arrayOf(PropTypes.string),
  currentDay: PropTypes.string.isRequired,
  currentTimeOverride: PropTypes.string,
  currentTimezone: PropTypes.string.isRequired,
  eventsByDay: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string.isRequired,
    events: PropTypes.arrayOf(eventPropType.isRequired).isRequired,
  })).isRequired,
};

export default EventListing;
